import React from 'react'
import AgTechStandardTabPage from '@agtech/web/components/Pages/Tabbed/AgTechTabPage';
import { SaleContactDetailsProvider } from '@sales/data/operation/contacts/SaleContactDataQueries'
import { getAvailableContactCredits, getInvoicedContactCredits, getExpiredContactCredits } from '@sales/data/operation/contacts/credits/ContactCreditData';
import SaleContactCreditsGrid from '../components/grids/SaleContactCreditsGrid';
import ContactCreditDialog from '../dialogs/credits/SaleContactCreditDialog';
import { SaleContactDetails } from '@sales/data/operation/contacts/SaleContactData';
import AgTechHelpModal from '@agtech/web/components/Portals/Modals/AgTechHelpModal';

const SaleContactCreditsTabPage = (props: { contactId: number }) => {
    return (
        <SaleContactDetailsProvider contactId={props.contactId}>
            {contactDetails => {
                return (
                    <AgTechStandardTabPage
                        {...getSaleContactCreditsTabPageProps({ contactDetails })}
                    />
                )
            }}
        </SaleContactDetailsProvider>
    )
}

const CreditsHelpButton = () => (
    <AgTechHelpModal.Button
        title='Contact Credits'
        content={
            <div className='row-based gap-y-3 flex'>
                <p>Credits are pre-paid amounts that can be applied against a contact's invoices. Once a credit is used on an invoice it moves to the <strong>Invoiced</strong> tab, and credits past their expiry date appear under <strong>Expired</strong>.</p>
                <p>Click <strong>+ Create</strong> to add a new credit for this contact, or click any row to edit an existing one.</p>
            </div>
        }
    />
);

export const getSaleContactCreditsTabPageProps = (props: {
    contactDetails: SaleContactDetails
}) => {
    const contact = props.contactDetails.contact;
    const contactCredits = Object.values(props.contactDetails.credits);

    const availableCredits = getAvailableContactCredits(contactCredits);
    const invoicedCredits = getInvoicedContactCredits(contactCredits);
    const expiredCredits = getExpiredContactCredits(contactCredits);

    return {
        tabs: [
            {
                text: `Available (${availableCredits.length})`,
                content: () => (
                    <SaleContactCreditsGrid
                        key={'Available'}
                        contact={contact}
                        credits={availableCredits}
                        modifyGridProps={contactGridProps => ({
                            ...contactGridProps,
                            header: {
                                title: 'Credits',
                                content: () => <><ContactCreditDialog.Button contactId={contact.id} /><CreditsHelpButton /></>
                            },
                            searchConfig: {
                                options: [
                                    { field: 'Amount', value: credit => credit.creditAmount.toString() },
                                    { field: 'Description', value: credit => credit.description }
                                ]
                            }
                        })}
                    />
                )
            },
            {
                text: `Invoiced (${invoicedCredits.length})`,
                content: () => (
                    <SaleContactCreditsGrid
                        key={'Invoiced'}
                        contact={contact}
                        credits={invoicedCredits}
                        modifyGridProps={contactGridProps => ({
                            ...contactGridProps,
                            header: {
                                title: 'Credits',
                                content: () => <><ContactCreditDialog.Button contactId={contact.id} /><CreditsHelpButton /></>
                            },
                            columns: [
                                ...contactGridProps.columns,
                                {
                                    // TODO: Add invoice info dialog button
                                    header: 'Invoice',
                                    value: (credit) => credit.invoiceId ? (
                                        <></>
                                        // <InvoiceInfoDialog.Button
                                        //     invoiceId={credit.invoiceId}
                                        //     classes='link'
                                        //     text='View Invoice'
                                        //     showNextSteps={false}
                                        // />
                                    ) : undefined,
                                    showHeader: false,
                                    width: '140px'
                                }
                            ],
                            searchConfig: {
                                options: [
                                    { field: 'Amount', value: credit => credit.creditAmount.toString() },
                                    { field: 'Description', value: credit => credit.description }
                                ]
                            }
                        })}
                    />
                )
            },
            {
                text: `Expired (${expiredCredits.length})`,
                content: () => (
                    <SaleContactCreditsGrid
                        key={'Expired'}
                        contact={props.contactDetails.contact}
                        credits={expiredCredits}
                        modifyGridProps={contactGridProps => ({
                            ...contactGridProps,
                            header: {
                                title: 'Credits',
                                content: () => <><ContactCreditDialog.Button contactId={contact.id} /><CreditsHelpButton /></>
                            },
                            searchConfig: {
                                options: [
                                    { field: 'Amount', value: credit => credit.creditAmount.toString() },
                                    { field: 'Description', value: credit => credit.description }
                                ]
                            }
                        })}
                    />
                )
            }
        ]
    }
}

export default SaleContactCreditsTabPage